import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Loader2, CheckCircle, Sparkles, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CompleteRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const transactionRef = searchParams.get('reference');
  const emailFromUrl = searchParams.get('email') || '';
  const challengeIdFromUrl = searchParams.get('challenge_id') || '';
  const productTypeFromUrl = searchParams.get('product_type') || '';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: emailFromUrl,
    password: '',
    confirmPassword: ''
  });
  
  useEffect(() => {
    if (transactionRef) {
      loadTransactionInfo();
    } else {
      setLoading(false);
    }
  }, [transactionRef]);
  
  const loadTransactionInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/transaction/${transactionRef}`);
      setTransaction(response.data);
      setFormData(prev => ({
        ...prev,
        email: response.data.user_email || emailFromUrl,
        name: response.data.user_name || ''
      }));
    } catch (error) {
      console.error('Error loading transaction:', error);
      toast.error('No se pudo cargar la información de la compra');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register-from-payment`, {
        name: formData.name,
        email: formData.email.toLowerCase(),
        password: formData.password,
        transaction_reference: transactionRef
      });
      
      if (response.data.token) {
        localStorage.setItem('session_token', response.data.token);
        toast.success('¡Cuenta creada exitosamente!');
        
        // Redirigir según el tipo de compra - usar URL params como fallback
        let redirectTo = response.data.redirect_to || '/dashboard';
        
        // Si el backend no devolvió redirect_to pero tenemos challenge_id de la URL, usar eso
        if (redirectTo === '/dashboard' && challengeIdFromUrl) {
          redirectTo = `/challenge/${challengeIdFromUrl}`;
        }
        
        navigate(redirectTo);
      } else {
        toast.success('Cuenta creada. Por favor inicia sesión.');
        navigate('/login');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cuenta';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-purple-500/10" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Card principal */}
        <div className="bg-zinc-900/90 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 shadow-2xl">
          {/* Header con icono */}
          <div className="text-center mb-8">
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full animate-pulse" />
              <div className="absolute inset-1 bg-zinc-900 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-pink-500" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">¡Un paso más!</h1>
            <p className="text-zinc-400">
              Crea tu cuenta para acceder a tu compra
            </p>
          </div>
          
          {/* Info de la compra */}
          {transaction && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Pago confirmado</span>
              </div>
              <p className="text-sm text-zinc-300">
                {transaction.metadata?.challenge_title || transaction.metadata?.product_name || 'Tu compra'}
              </p>
            </div>
          )}
          
          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nombre completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Tu nombre"
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="register-name-input"
                />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="tu@email.com"
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>
            
            {/* Contraseña */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Contraseña *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {/* Confirmar Contraseña */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Confirmar contraseña *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Repite tu contraseña"
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>
            
            {/* Botón de submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-pink-500/30 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 mt-6"
              data-testid="complete-registration-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creando cuenta...
                </span>
              ) : (
                'Crear mi cuenta y acceder'
              )}
            </Button>
          </form>
          
          {/* Link a login */}
          <p className="text-center text-zinc-500 text-sm mt-6">
            ¿Ya tienes cuenta?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-pink-400 hover:text-pink-300 font-medium"
            >
              Inicia sesión
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteRegistration;
